import { useCallback, useEffect, useRef, useState } from 'react';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';
import { createComponentLogger } from '@/utils/logger';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import {
  getDisclosureReminderPreferences,
  setDisclosureReminderPaused,
  type DisclosureReminderPreference
} from '@/services/disclosureReminderPreferencesService';

const log = createComponentLogger('DisclosureReminderControls', 'loan-communications');

export function DisclosureReminderControls({ loanId }: { loanId: string }) {
  const flag = useFeatureFlag('borrower_disclosure_reminder_pause_v1');
  // An absent/disabled flag or unresolved read must never mount the RPC client.
  return !flag.loading && flag.enabled ? <ReminderPreferences key={loanId} loanId={loanId} /> : null;
}

function ReminderPreferences({ loanId }: { loanId: string }) {
  const [preferences, setPreferences] = useState<DisclosureReminderPreference[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const [status, setStatus] = useState('');
  const generation = useRef(0);
  const savingRef = useRef(false);

  const load = useCallback(async () => {
    const request = ++generation.current;
    setPreferences(null);
    setError(null);
    setStatus('');
    try {
      const rows = await getDisclosureReminderPreferences(loanId);
      if (generation.current === request) {
        setPreferences(rows);
      }
    } catch {
      if (generation.current === request) {
        log.error('Unable to load reminder preferences', { operation: 'load' });
        setError('Unable to load disclosure reminder settings.');
      }
    }
  }, [loanId]);

  useEffect(() => {
    setSaving(null);
    setStatus('');
    void load();
    return () => {
      generation.current += 1;
    };
  }, [load]);

  async function toggle(row: DisclosureReminderPreference) {
    if (savingRef.current) {
      return;
    }
    savingRef.current = true;
    const request = generation.current;
    setSaving(row.recipient_email);
    setError(null);
    setStatus('');
    try {
      const updatedAt = await setDisclosureReminderPaused(loanId, row.recipient_email, !row.paused, row.updated_at);
      if (generation.current !== request) {
        return;
      }
      setPreferences(current => current?.map(item => item.recipient_email === row.recipient_email
        ? { ...item, paused: !row.paused, updated_at: updatedAt } : item) ?? null);
      setStatus(`Disclosure reminders ${row.paused ? 'resumed' : 'paused'} for ${row.recipient_email}.`);
    } catch {
      if (generation.current === request) {
        log.error('Unable to save reminder preference', { operation: 'save' });
        setError('Unable to save. Refresh to check the current setting before trying again.');
      }
    } finally {
      if (generation.current === request) {
        savingRef.current = false;
        setSaving(null);
      }
    }
  }

  return (
    <section aria-label="Automatic disclosure reminders" className="mb-6 rounded-lg border bg-card p-4 text-card-foreground">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Automatic disclosure reminders</h3>
        <Button type="button" variant="outline" disabled={saving !== null || (!preferences && !error)} onClick={() => void load()}>Refresh</Button>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        Automatic daily signing reminders by email address for this loan. Manual disclosure sends are unchanged. Pausing the main recipient also stops the team copies of that reminder. Separate internal emails, including LE notices, are unchanged.
      </p>
      {error && <div role="alert" className="mt-3 text-sm text-destructive">
        {error}
      </div>}
      {!preferences && !error && <p role="status" className="mt-3 text-sm text-muted-foreground">Loading reminder settings…</p>}
      {preferences?.length === 0 && <p className="mt-3 text-sm text-muted-foreground">No borrower email addresses are available for this loan.</p>}
      {preferences?.map(row => (
        <div key={row.recipient_email} className="mt-3 flex min-w-0 flex-wrap items-center justify-between gap-3 border-t pt-3">
          <span className="min-w-0 break-all text-sm">{row.recipient_email}</span>
          <div className="flex shrink-0 items-center gap-2">
            <span className="text-sm text-muted-foreground">{saving === row.recipient_email ? 'Saving…' : error ? 'Unconfirmed — refresh' : row.paused ? 'Paused' : 'Allowed'}</span>
            <Switch aria-label={`Disclosure reminders for ${row.recipient_email}`} checked={!row.paused}
              disabled={saving !== null || error !== null} onCheckedChange={() => void toggle(row)} />
          </div>
        </div>
      ))}
      <p className="mt-3 text-xs text-muted-foreground">A pause applies only to this email address on this loan, including new disclosure packages, until resumed. If the borrower’s email changes, pause the new address separately.</p>
      <p className="mt-2 text-xs text-muted-foreground">Allowed means the address is not paused; existing sending rules still apply. Co-borrowers do not receive a separate scheduled reminder. A pause also removes the address from copies. An email already being sent may still arrive.</p>
      <p role="status" className="sr-only">{status}</p>
    </section>
  );
}
